import '../../i18n/autoTranslate';
import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';

const SearchBar = ({ 
  onSearch, 
  onFilterToggle,
  placeholder = "Search resources, topics, or skills...",
  className = "" 
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchRef = useRef(null);

  // Real search suggestions aligned with the built-in library dataset.
  const mockSuggestions = [
    { id: 1, text: 'Full Stack Developer SaaS interview questions', type: 'popular', icon: 'Code' },
    { id: 2, text: 'Frontend React JavaScript interview questions', type: 'popular', icon: 'Monitor' },
    { id: 3, text: 'Backend API SQL system design', type: 'trending', icon: 'Server' },
    { id: 4, text: 'System design interview video', type: 'trending', icon: 'Network' },
    { id: 5, text: 'STAR behavioral interview method', type: 'popular', icon: 'MessageSquare' },
    { id: 6, text: 'Product Manager interview questions', type: 'recent', icon: 'Briefcase' },
    { id: 7, text: 'Data Analyst SQL case interview', type: 'recent', icon: 'BarChart3' },
    { id: 8, text: 'DevOps CI/CD monitoring interview', type: 'trending', icon: 'CloudCog' },
    { id: 9, text: 'QA E2E regression testing interview', type: 'recent', icon: 'Bug' },
    { id: 10, text: 'UX Designer product case interview', type: 'popular', icon: 'PenTool' }
  ];

  useEffect(() => {
    if (searchQuery?.length > 2) {
      const filtered = mockSuggestions?.filter(suggestion =>
        suggestion?.text?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      );
      setSuggestions(filtered);
      setShowSuggestions(true);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef?.current && !searchRef?.current?.contains(event?.target)) {
        setShowSuggestions(false);
        setIsSearchFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (query = searchQuery) => {
    if (onSearch) {
      onSearch(query);
    }
    setShowSuggestions(false);
    setIsSearchFocused(false);
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchQuery(suggestion?.text);
    handleSearch(suggestion?.text);
  };

  const handleKeyPress = (e) => {
    if (e?.key === 'Enter') {
      handleSearch();
    }
  };

  const getSuggestionTypeColor = (type) => {
    switch (type) {
      case 'popular':
        return 'text-accent';
      case 'trending':
        return 'text-primary';
      case 'recent':
        return 'text-success';
      default:
        return 'text-muted-foreground';
    }
  };

  const getSuggestionTypeLabel = (type) => {
    switch (type) {
      case 'popular':
        return 'Popular';
      case 'trending':
        return 'Trending';
      case 'recent':
        return 'Recent';
      default:
        return '';
    }
  };

  return (
    <div ref={searchRef} className={`relative ${className}`}>
      {/* Search Input Container */}
      <div className={`
        relative glass rounded-xl border transition-all duration-200
        ${isSearchFocused 
          ? 'border-accent/50 shadow-glow' 
          : 'border-glass-border hover:border-accent/30'
        }
      `}>
        <div className="flex items-center">
          {/* Search Icon */}
          <div className="pl-4 pr-2">
            <Icon 
              name="Search" 
              size={20} 
              className={`transition-colors duration-200 ${
                isSearchFocused ? 'text-accent' : 'text-muted-foreground'
              }`} 
            />
          </div>

          {/* Search Input */}
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e?.target?.value)}
            onFocus={() => setIsSearchFocused(true)}
            onKeyPress={handleKeyPress}
            placeholder={placeholder}
            className="flex-1 bg-transparent text-foreground placeholder-muted-foreground py-3 pr-4 focus:outline-none"
          />

          {/* Clear Button */}
          {searchQuery && (
            <button
              onClick={() => {
                setSearchQuery('');
                handleSearch('');
              }}
              className="px-2 text-muted-foreground hover:text-foreground transition-colors duration-150"
            >
              <Icon name="X" size={16} />
            </button>
          )}

          {/* Filter Toggle */}
          <button
            onClick={onFilterToggle}
            className="px-4 py-3 text-muted-foreground hover:text-accent transition-colors duration-150 border-l border-glass-border"
          >
            <Icon name="SlidersHorizontal" size={20} />
          </button>
        </div>
      </div>
      {/* Search Suggestions */}
      {showSuggestions && suggestions?.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-2 glass rounded-xl border border-glass-border z-50 animate-fade-in">
          <div className="p-2">
            {/* Suggestions Header */}
            <div className="px-3 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Suggestions
            </div>

            {/* Suggestion Items */}
            {suggestions?.map((suggestion) => (
              <button
                key={suggestion?.id}
                onClick={() => handleSuggestionClick(suggestion)}
                className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-left hover:bg-muted/30 transition-colors duration-150 group"
              >
                <Icon 
                  name={suggestion?.icon} 
                  size={16} 
                  className="text-muted-foreground group-hover:text-accent transition-colors duration-150" 
                />
                
                <div className="flex-1">
                  <div className="text-sm text-foreground group-hover:text-accent transition-colors duration-150">
                    {suggestion?.text}
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <span className={`text-xs font-medium ${getSuggestionTypeColor(suggestion?.type)}`}>
                    {getSuggestionTypeLabel(suggestion?.type)}
                  </span>
                  <Icon name="ArrowUpRight" size={12} className="text-muted-foreground" />
                </div>
              </button>
            ))}
          </div>

          {/* Quick Actions */}
          <div className="border-t border-glass-border p-2">
            <div className="px-3 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Quick Actions
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button className="flex items-center space-x-2 px-3 py-2 rounded-lg text-left hover:bg-muted/30 transition-colors duration-150 group">
                <Icon name="TrendingUp" size={14} className="text-accent" />
                <span className="text-xs text-foreground">Popular Now</span>
              </button>
              <button className="flex items-center space-x-2 px-3 py-2 rounded-lg text-left hover:bg-muted/30 transition-colors duration-150 group">
                <Icon name="Clock" size={14} className="text-primary" />
                <span className="text-xs text-foreground">Recent</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchBar;
